// Angular
import { Component, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NgClass, TitleCasePipe } from '@angular/common';
import { FormGroup, FormControl, Validators, ReactiveFormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

// Angular Material
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialog } from '@angular/material/dialog';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';

// Third Party
import { filter, finalize, Subject, tap } from 'rxjs';

// Services
import { ReleaseNotesService } from '@services/release-notes.service';

// Models
import {
  FileUpload,
  PresignedURLObject,
  ProductListResponse,
  ProductVersionInfo,
  ReleaseNotes,
  ReleaseNotesFormValue,
} from '@components/release-notes';

// Constants
import { Action, MediaType, Status } from '@constants/enum';
import {
  IMAGE_MAX_SIZE,
  IMAGE_TYPES,
  MY_DATE_FORMATS,
  VIDEO_MAX_SIZE,
  VIDEO_TYPES,
} from '@components/release-notes/defs';

// Pipes
import { FormatProductNamePipe } from '@utils/format-product-name.pipe';

// Dialogs
import { SuccessDialogComponent } from '@components/dialogs';

// Components
import { LoadingSpinnerComponent, FormErrorComponent } from '@components/common-components';
import { CustomDateAdapter } from '@utils/custom-date-adaptor';

@Component({
  templateUrl: './release-notes-form.component.html',
  styleUrl: './release-notes-form.component.scss',
  imports: [
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatButtonModule,
    MatCardModule,
    MatSidenavModule,
    MatDatepickerModule,
    NgClass,
    FormatProductNamePipe,
    TitleCasePipe,
    LoadingSpinnerComponent,
    FormErrorComponent,
  ],
  providers: [
    { provide: DateAdapter, useClass: CustomDateAdapter },
    { provide: MAT_DATE_LOCALE, useValue: 'en-GB' },
    { provide: MAT_DATE_FORMATS, useValue: MY_DATE_FORMATS },
  ],
})
export class ReleaseNotesFormComponent implements OnInit {
  status = Status;
  readonly selectedProduct = signal<string>(null);
  readonly sharedReleaseNotes = signal<ReleaseNotes>(null);
  readonly pageType = signal<string>(null);
  readonly loading = signal(true);
  readonly startingForm = signal<ReleaseNotesFormValue>(null);
  readonly products = signal<string[]>([]);
  readonly releaseId = signal<string>(null);
  readonly isPreview = signal(false);
  readonly formError = signal(false);
  readonly action = Action;

  readonly releaseNotesForm = new FormGroup({
    releaseId: new FormControl(null),
    title: new FormControl('', { validators: [Validators.required] }),
    version: new FormControl('', { validators: [Validators.required] }),
    summaryText: new FormControl('', { validators: [Validators.required] }),
    tentativeReleaseAt: new FormControl(null, { validators: [Validators.required] }),
    productId: new FormControl(null, [Validators.required]),
    fileUpload: new FormControl(null, [Validators.required]),
  });

  readonly subjReload = new Subject<void>();

  // Input field focus
  readonly focusState = signal<Record<string, boolean>>({});
  readonly isViewed = signal(false);
  readonly productVersionExists = signal(false);
  readonly productVersionExistsMessage = signal('');
  readonly uploadedFiles = signal<FileUpload[]>([]);
  readonly fileUploadError = signal('');
  readonly uploadDetail = signal({
    uploadUrl: '',
    objectKey: '',
    file: null as File | null,
  });
  readonly isFileUploadDisabled = signal(true);

  private readonly releaseNotesService = inject(ReleaseNotesService);
  public matDialog = inject(MatDialog);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly destroyRef = inject(DestroyRef);

  ngOnInit() {
    this.getAllProductList();
    this.route.paramMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) => {
      const newReleaseId = params.get('id');
      // Only reload if releaseId changed or it's the first load
      if (this.releaseId() !== newReleaseId) {
        this.releaseId.set(newReleaseId);
        this.loadReleaseNotes();
      }
    });
  }

  loadReleaseNotes() {
    if (this.router.url.includes(Action.edit) || this.router.url.includes(Action.view)) {
      this.loading.set(true);
      this.pageType.set(this.router.url.includes(Action.edit) ? Action.edit : Action.view);
      this.isViewed.set(this.pageType() === Action.view);
      this.releaseNotesService
        .getReleaseNotesById(this.releaseId())
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (response: ReleaseNotes) => {
            if (response) {
              // Populating form with existing values
              this.sharedReleaseNotes.set(response);
              this.releaseNotesForm.patchValue({
                releaseId: this.sharedReleaseNotes().releaseId,
                productId: this.sharedReleaseNotes().productId,
                title: this.sharedReleaseNotes().title,
                tentativeReleaseAt: new Date(+this.sharedReleaseNotes().tentativeReleaseAt),
                version: this.sharedReleaseNotes().version,
                summaryText: this.sharedReleaseNotes().summaryText,
              });

              // Preparing values for dropdowns
              this.selectedProduct.set(response.productId);

              // File upload - display existing media from CloudFront
              if (response.mediaUrl) {
                this.isPreview.set(true);
                const fileExtension = response.mediaUrl.split('.').pop()?.toLowerCase();
                this.uploadedFiles.set([
                  {
                    name: response.mediaUrl.split('/').pop(),
                    type:
                      response.mediaType === MediaType.image
                        ? `image/${fileExtension}`
                        : 'video/mp4',
                    preview: response.mediaUrl,
                    file: null,
                  },
                ]);
                this.releaseNotesForm.patchValue({ fileUpload: 'existing' });
              } else {
                this.uploadedFiles.set([]);
              }

              // Taking a snapshot before enable/disable to avoid capturing disabled state
              this.startingForm.set(structuredClone(this.releaseNotesForm.getRawValue()));

              // Enable/disable form controls based on view mode
              if (this.isViewed()) {
                this.releaseNotesForm.disable();
              } else {
                this.releaseNotesForm.enable();
                // In edit mode, check if file upload should remain enabled
                this.checkFileUploadEnable();
              }
            }
            this.loading.set(false);
          },
          error: () => {
            this.loading.set(false);
            this.formError.set(true);
          },
        });
    } else {
      this.pageType.set(Action.create);
      // Taking a snapshot for create mode
      this.startingForm.set(structuredClone(this.releaseNotesForm.getRawValue()));
    }
  }

  getAllProductList() {
    this.loading.set(true);
    this.releaseNotesService
      .getAllProductList()
      .pipe(
        filter((response) => !!response),
        tap((response: ProductListResponse) => this.products.set(response.products)),
        finalize(() => this.loading.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  onSubmit() {
    if (this.releaseNotesForm.valid) {
      this.loading.set(true);
      const formValues = this.releaseNotesForm.getRawValue();
      const isProductOrVersionChanged =
        this.pageType() === Action.edit &&
        (this.sharedReleaseNotes()?.productId !== formValues.productId ||
          this.sharedReleaseNotes()?.version !== formValues.version);
      const isNewFileUploaded = this.uploadDetail().file !== null;

      if (this.pageType() === Action.create || isNewFileUploaded) {
        // Upload to S3 if creating new record or new file uploaded
        this.releaseNotesService
          .uploadToPresignedUrl(this.uploadDetail().uploadUrl, this.uploadDetail().file)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe({
            next: (uploadResponse) => {
              if (uploadResponse.status === 200) {
                this.saveReleaseNotes();
              }
            },
            error: () => {
              this.loading.set(false);
            },
          });
      } else if (isProductOrVersionChanged) {
        // Product or version changed but file is same - fetch file from mediaUrl and upload to new path
        const existingFile = this.uploadedFiles()[0];

        // Use service to fetch the file as blob
        this.releaseNotesService
          .fetchFileAsBlob(this.sharedReleaseNotes().mediaUrl)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe({
            next: (blob) => {
              const contentType = blob.type || existingFile.type;
              const file = new File([blob], existingFile.name, { type: contentType });

              const releaseNotesDetail: ProductVersionInfo = {
                product: formValues.productId,
                version: formValues.version,
                fileName: existingFile.name,
                releaseId: this.sharedReleaseNotes().releaseId,
              };

              // Get new presigned URL
              this.releaseNotesService
                .getURLFromS3(releaseNotesDetail)
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe({
                  next: (response: PresignedURLObject) => {
                    if (response?.isSuccess === true && response?.uploadUrl) {
                      this.uploadDetail.set({
                        uploadUrl: response.uploadUrl,
                        objectKey: response.objectKey,
                        file: file,
                      });

                      // Upload file to new S3 path
                      this.releaseNotesService
                        .uploadToPresignedUrl(this.uploadDetail().uploadUrl, file)
                        .pipe(takeUntilDestroyed(this.destroyRef))
                        .subscribe({
                          next: (uploadResponse) => {
                            if (uploadResponse.status === 200) {
                              this.saveReleaseNotes();
                            }
                          },
                          error: () => {
                            this.loading.set(false);
                          },
                        });
                    } else if (response?.isSuccess === false) {
                      this.loading.set(false);
                      this.productVersionExists.set(true);
                      this.productVersionExistsMessage.set(response.message);
                      return;
                    }
                  },
                  error: () => {
                    this.loading.set(false);
                  },
                });
            },
            error: () => {
              this.loading.set(false);
            },
          });
      } else {
        // No upload needed, just save the release notes
        this.saveReleaseNotes();
      }
    } else {
      this.releaseNotesForm.markAllAsTouched();
      if (this.uploadedFiles().length === 0) {
        this.fileUploadError.set('You must upload a file.');
        return;
      }
    }
  }

  saveReleaseNotes() {
    const file = this.uploadDetail().file;
    const type = file
      ? file.type.startsWith('image')
        ? MediaType.image
        : file.type.startsWith('video')
          ? MediaType.video
          : MediaType.audio
      : this.sharedReleaseNotes()?.mediaType;
    const formValues = this.releaseNotesForm.getRawValue();

    const releaseNotes: ReleaseNotes = {
      releaseId: this.pageType() === Action.create ? null : this.sharedReleaseNotes()?.releaseId,
      productId: formValues.productId,
      title: formValues.title,
      tentativeReleaseAt: this.convertToUtcEpoch(formValues.tentativeReleaseAt),
      version: formValues.version,
      summaryText: formValues.summaryText,
      updatedBy: '',
      mediaType: type,
      mediaS3Path: this.uploadDetail().objectKey || this.sharedReleaseNotes()?.mediaS3Path,
      mediaUrl: '',
      modifiedBy: '',
      modifiedDate: '',
      status: Status.draft,
    };

    const request =
      this.pageType() === Action.create
        ? this.releaseNotesService.createReleaseNotes(releaseNotes)
        : this.releaseNotesService.updateReleaseNotes(releaseNotes);

    request
      .pipe(
        tap(() => this.openSuccessDialog(this.pageType())),
        finalize(() => this.loading.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  convertToUtcEpoch(date: Date): string {
    const selectedDate = new Date(date);
    const utcDate = new Date(
      Date.UTC(
        selectedDate.getFullYear(),
        selectedDate.getMonth(),
        selectedDate.getDate(),
        0,
        0,
        0,
      ),
    );
    return utcDate.getTime().toString();
  }

  openSuccessDialog(pageType: string) {
    const updatedType = pageType === Action.create ? 'created' : 'updated';
    const successDialog = this.matDialog.open(SuccessDialogComponent, {
      data: {
        title: `Release Notes ${updatedType}!`,
        message: `You have successfully ${updatedType} some new ACETECH™ release notes.`,
        button: 'Great!',
      },
      disableClose: true,
    });
    successDialog
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.router.navigate(['/release-notes']);
      });
  }

  onProductChange(value: string) {
    this.selectedProduct.set(value);
    this.releaseNotesForm.get('productId').setValue(this.products().find((el) => el === value));
    this.checkFileUploadEnable();
  }

  checkFileUploadEnable() {
    this.productVersionExists.set(false);
    const product = this.releaseNotesForm.get('productId').value;
    const version = this.releaseNotesForm.get('version').value;
    this.isFileUploadDisabled.set(!(product && version));

    // Don't disable fileUpload control in edit mode when product and version exist
    if (!this.isViewed() && product && version) {
      this.releaseNotesForm.get('fileUpload').enable();
    }
  }

  getErrorMessage(name: string) {
    switch (name) {
      case 'summaryText': {
        if (this.releaseNotesForm.controls['summaryText'].touched) {
          return 'You must enter release notes.';
        }
        return 'Error';
      }
      case 'tentativeReleaseAt': {
        if (this.releaseNotesForm.controls['tentativeReleaseAt'].touched) {
          return 'You must select a release date.';
        }
        return 'Error';
      }
      case 'productId': {
        if (this.releaseNotesForm.controls['productId'].touched) {
          return 'You must select a product.';
        }
        return 'Error';
      }
      case 'title': {
        if (this.releaseNotesForm.controls['title'].touched) {
          return 'You must enter a title.';
        }
        return 'Error';
      }
      case 'version': {
        if (this.releaseNotesForm.controls['version'].touched) {
          return 'You must enter a version.';
        }
        return 'Error';
      }
    }
    return null;
  }

  // Used to prevent errors appearing too soon
  onInputFocus(formValue: string) {
    this.focusState.update((state) => ({ ...state, [formValue]: true }));
  }

  onInputBlur(formValue: string) {
    this.focusState.update((state) => ({ ...state, [formValue]: false }));
    if (formValue === 'version') {
      this.productVersionExists.set(false);
      this.checkFileUploadEnable();
    }
  }

  close() {
    this.router.navigate(['/release-notes']);
  }

  onFileSelected(event: Event) {
    const target = event.target as HTMLInputElement;
    const files = target.files;
    if (files) {
      this.handleFiles(files);
    }
    target.value = '';
  }

  onFileDrop(event: DragEvent) {
    event.preventDefault();
    const files = event.dataTransfer?.files;
    if (files) {
      this.handleFiles(files);
    }
  }

  onDragOver(event: DragEvent) {
    event.preventDefault();
  }

  handleFiles(files: FileList) {
    if (files.length > 0) {
      const file = files[0];
      if (IMAGE_TYPES.includes(file.type)) {
        if (file.size > IMAGE_MAX_SIZE) {
          this.fileUploadError.set('Image size must not exceed 4MB.');
          return;
        }
      } else if (VIDEO_TYPES.includes(file.type)) {
        if (file.size > VIDEO_MAX_SIZE) {
          this.fileUploadError.set('Video size must not exceed 20MB.');
          return;
        }
      } else {
        this.fileUploadError.set('Only JPG/PNG images or MP4 videos are allowed.');
        return;
      }

      this.fileUploadError.set('');
      this.isPreview.set(false);
      const reader = new FileReader();
      reader.onload = (e: ProgressEvent<FileReader>) => {
        this.uploadedFiles.set([
          {
            name: file.name,
            type: file.type,
            preview: e.target?.result as string,
            file: file,
          },
        ]);
        this.releaseNotesForm.patchValue({ fileUpload: file });
        this.uploadToS3(file);
      };
      reader.readAsDataURL(file);
    }
  }

  uploadToS3(file: File) {
    this.loading.set(true);
    this.productVersionExists.set(false);
    const releaseNotesDetail: ProductVersionInfo = {
      product: this.selectedProduct(),
      version: this.releaseNotesForm.get('version').value,
      fileName: file.name,
      releaseId: this.sharedReleaseNotes() ? this.sharedReleaseNotes().releaseId : null,
    };

    this.releaseNotesService
      .getURLFromS3(releaseNotesDetail)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response: PresignedURLObject) => {
          this.loading.set(false);
          if (response?.isSuccess === true && response?.uploadUrl) {
            this.isPreview.set(true);
            this.uploadDetail.set({
              uploadUrl: response.uploadUrl,
              objectKey: response.objectKey,
              file: file,
            });
          } else if (response?.isSuccess === false) {
            this.isPreview.set(false);
            this.productVersionExists.set(true);
            this.productVersionExistsMessage.set(response.message);
            this.releaseNotesForm.patchValue({ fileUpload: null });
            this.uploadedFiles.set([]);
          }
        },
        error: () => {
          this.loading.set(false);
        },
      });
  }

  removeFile(index: number) {
    this.uploadedFiles.update((files) => files.filter((_, i) => i !== index));
    this.releaseNotesForm.patchValue({ fileUpload: null });
  }

  navigateToEditScreen() {
    // Update state without navigation to avoid component reload
    this.pageType.set(Action.edit);
    this.isViewed.set(false);
    this.releaseNotesForm.enable();
    this.checkFileUploadEnable();
    // Update URL without reloading component
    this.router.navigate(['/release-notes', this.action.edit, this.releaseId()], {
      replaceUrl: true,
    });
  }
}
